import { useTranslation } from 'react-i18next';
import { Text, Input } from '@/ui';
import VerticalDivider from '@/ui/VerticalDivider/VerticalDivider';
import styles from './styles.module.css';
import useFormatNumber from '@/hooks/useFormatNumber';
import { formatCurrency } from '@/utils/currency';

type ReportCalculations = {
  currentResidual: number;
  transactionCount: number;
  paymentVolume: number;
  totalFee: number;
  finalFee: number;
};

type Props = {
  reportCalculations: ReportCalculations;
  handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  hasResiduals: boolean;
};

function CurrentResiduals({ reportCalculations, handleSearch, hasResiduals }: Props) {
  const { t } = useTranslation();
  const formatValue = useFormatNumber();

  const { currentResidual, transactionCount, paymentVolume, totalFee, finalFee } =
    reportCalculations;

  return (
    <>
      <div className={styles.aggregateContainer}>
        <div className={styles.aggregateLeft}>
          <div>
            <Text size='m' weight='semi-bold'>
              {t('Residuals.current_residual')}
            </Text>
            <Text size='s' weight='light-medium'>
              {t('Residuals.current_residual_description')}
            </Text>
          </div>
          <div className={styles.residualAmountSection}>
            <Text size='xxl' weight='medium'>
              {formatValue(currentResidual, false, 0)}/5000
            </Text>
            <div className={styles.progressBar}>
              <div
                className={styles.progressBarFill}
                style={{
                  width: `${Math.min((currentResidual / 5000) * 100, 100)}%`,
                }}
              />
            </div>
          </div>
        </div>
        <VerticalDivider height='100%' />
        <div className={styles.aggregateRight}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text size='m' weight='light-medium'>
              {t('Residuals.total_residual_earned')}:
            </Text>
            <Text size='m' weight='light-medium'>
              {formatCurrency(currentResidual, 'USD')}
            </Text>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text size='m' weight='light-medium'>
              {t('Residuals.total_transaction_count')}:
            </Text>
            <Text size='m' weight='light-medium'>
              {formatValue(transactionCount, false, 0)}
            </Text>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text size='m' weight='light-medium'>
              {t('Residuals.total_payment_volume')}:
            </Text>
            <Text size='m' weight='light-medium'>
              {formatCurrency(paymentVolume, 'USD')}
            </Text>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text size='m' weight='light-medium'>
              {t('Residuals.total_fee_collected')}:
            </Text>
            <Text size='m' weight='light-medium'>
              {formatCurrency(totalFee, 'USD')}
            </Text>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text size='m' weight='light-medium'>
              {t('Residuals.total_final_fee')}:
            </Text>
            <Text size='m' weight='light-medium'>
              {formatCurrency(finalFee, 'USD')}
            </Text>
          </div>
        </div>
      </div>
      <Text size='m' weight='semi-bold'>
        {t('Residuals.breakdown')}
      </Text>
      {hasResiduals && (
        <div className={styles.searchInput}>
          <Input
            size='small'
            variant={'search'}
            placeholder={t('Residuals.search_placeholder')}
            onChange={handleSearch}
          />
        </div>
      )}
    </>
  );
}

export default CurrentResiduals;
